import { AugmentedRenderItemInfo, EmptyPlaceholderProps } from '@codeleap/web'
import { useVirtualListContext } from './context'
import { EmotionJSX } from '@emotion/react/types/jsx-namespace'
import React, { useCallback, useEffect } from 'react'
import { VirtualItem } from '@tanstack/react-virtual'
import { EmptyPlaceholder } from '@/components'
import { PropsOf } from '@codeleap/types'

type Props<T> = {
  renderItem: (data: AugmentedRenderItemInfo<T>) => EmotionJSX.Element
}

export function VirtualListItems<T>(props: Props<T>) {
  const { renderItem: Item } = props
  const virtualizer = useVirtualListContext(v => v.virtualizer)
  const listItems = useVirtualListContext(v => v.list.data)
  const totalItemCount = useVirtualListContext(v => v.list.totalItems)
  const hasMore = useVirtualListContext(v => v.list.hasNextPage)

  const getNextPage = useVirtualListContext(v => v.loadNextPage)
  const isFetchingNextPage = useVirtualListContext(v => v.list.isFetchingNextPage)
  const PaginationIndicator = useVirtualListContext(v => v.list.ListFooterComponent)
  const lastItemIndex = useVirtualListContext(v => v.lastItemIndex)
  const paginationIndicatorIndex = useVirtualListContext(v => v.paginationIndicatorIndex)

  const items = useVirtualListContext(ctx => ctx.virtualizer.getVirtualItems())

  useEffect(() => {
    getNextPage()
  }, [hasMore, getNextPage, listItems.length, isFetchingNextPage, items])

  const ItemWrapper = useCallback(
    (props: React.PropsWithChildren<{ item: VirtualItem }>) => {
      const { item, children } = props
      return (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            // height: `${item.size}px`,
            transform: `translateY(${item.start}px)`,
          }}
          ref={virtualizer.measureElement}
          data-index={item.index}
        >
          {children}
        </div>
      )
    },
    [virtualizer],
  )

  const renderItem = useCallback(
    (item: VirtualItem) => {
      const data = listItems[item.index]

      const isLast = item.index == lastItemIndex

      const isPaginationIndicator = item.index == paginationIndicatorIndex

      if (isPaginationIndicator) {
        return (
          <ItemWrapper key={item.key} item={item}>
            <PaginationIndicator />
          </ItemWrapper>
        )
      }

      const content = (
        <Item
          data={data}
          index={item.index}
          item={data}
          isLast={isLast}
          isFirst={item.index == 0}
          isOnly={totalItemCount === 1}
          width={100}
        />
      )

      return (
        <ItemWrapper key={item.key} item={item}>
          {content}
        </ItemWrapper>
      )
    },
    [totalItemCount, listItems, hasMore, PaginationIndicator, paginationIndicatorIndex, lastItemIndex],
  )

  return (
    <div
      style={{
        height: `${virtualizer.getTotalSize()}px`,
        width: '100%',
        position: 'relative',
      }}
    >
      {items.map(renderItem)}
    </div>
  )
}

type ContainerProps = React.PropsWithChildren<React.ComponentPropsWithoutRef<'div'>> & {
  placeholder?: EmotionJSX.Element | Partial<PropsOf<typeof EmptyPlaceholder>>
}

export const VirtualListContainer = (props: ContainerProps) => {
  const { children, placeholder, ...rest } = props

  const scrollRef = useVirtualListContext(ctx => ctx.scrollRef)

  const totalItemCount = useVirtualListContext(ctx => ctx.list.totalItems)

  const listPlaceholder = useVirtualListContext(ctx => ctx.list.placeholder)

  if (!totalItemCount) {
    if (React.isValidElement(placeholder)) {
      return placeholder
    }

    return <EmptyPlaceholder {...listPlaceholder} {...placeholder} />
  }

  return (
    <div ref={scrollRef} {...rest}>
      {children}
    </div>
  )
}
